<template>
  <div>
    <router-view />
    <feedback />
    <alert />
    <modal />
    <prompt />
    <notification></notification>
  </div>
</template>

<script>
import Alert from '@/components/shared/Alert.vue';
import Modal from '@/components/shared/Modal.vue';
import Prompt from '@/components/shared/Prompt.vue';
import Feedback from '@/components/sections/feedback/FeedbackForm.vue';
import { activeDepartment$, currentUserIdentity$, isLoggedIn$ } from '@/services';
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { isInIframe, listenToParentMessages, exposeElementsToParent, notifyUrlChanges } from '@/utils/iframeUtils';
import mitt from 'mitt';

const GENESYS_SCRIPT_ID = 'genesys-messenger-bootstrap';
const GENESYS_COMMAND_NAME = 'Genesys';
const GENESYS_BOOTSTRAP_URL = 'https://apps.mypurecloud.de/genesys-bootstrap/genesys.min.js';
const GENESYS_LAUNCHER_HIDE_STYLE_ID = 'genesys-native-launcher-hide-style';
const GENESYS_CONFIG = {
  environment: 'prod-euc1',
  deploymentId: 'c2319dec-2fec-4119-b84f-ce388e594256'
};

export default {
  name: 'App',
  components: {
    Alert,
    Modal,
    Prompt,
    Feedback
  },
  setup() {
    const instance = getCurrentInstance();
    const $emitter = instance.appContext.config.globalProperties.$emitter;
    const store = useStore();
    const route = useRoute();
    const isLoggedIn = ref(false);
    const currentUserIdentity = ref(null);
    const activeDepartment = ref(null);
    const genesysSelectedTreatmentArea = ref(null);
    const genesysLauncherReady = ref(false);
    const genesysMessengerOpen = ref(false);
    const genesysMessengerOpening = ref(false);
    const genesysConversationActive = ref(false);
    const genesysSubscriptionsRegistered = ref(false);
    const subscriptions = [];


    const getDepartmentId = () => {
      const departmentId = activeDepartment.value?.ppwRecId;
      if (!departmentId) {
        return '00000000';
      }

      return departmentId.includes('-') ? departmentId.split('-')[1] : departmentId;
    };

    const activePatient = computed(() => store.getters['patient/getPatientGegevens']);
    const genesysPatientId = computed(() => {
      const isMainDashboard = route.name === 'main' || route.path === '/';
      const patientId = activePatient.value?.ExternalId;

      if (isMainDashboard || !patientId) {
        return null;
      }

      return patientId;
    });

    const genesysAttributes = computed(() => ({
      prescriberId: currentUserIdentity.value?.externalId || currentUserIdentity.value?.id || '00000000',
      departmentId: getDepartmentId(),
      email: currentUserIdentity.value?.email || '',
      name: currentUserIdentity.value?.simpleName || currentUserIdentity.value?.username || '',
      treatmentArea: genesysSelectedTreatmentArea.value || '',
      ...(genesysPatientId.value ? { patientId: genesysPatientId.value } : {})
    }));
    const genesysChatEnabled = computed(
      () =>
        currentUserIdentity.value?.featureFlags?.genesysChat ??
        instance.appContext.config.globalProperties.$featureFlag?.genesysChat ??
        false
    );

    const setGenesysConversationActive = (value) =>
      store.commit('navigation/setGenesysConversationActive', value);

    const enforceNativeGenesysLauncherVisibility = (isVisible) => {
      let styleTag = document.getElementById(GENESYS_LAUNCHER_HIDE_STYLE_ID);

      if (!isVisible) {
        const launcherHideCss = `
          #genesys-messenger #genesys-mxg-frame,
          #genesys-messenger .genesys-mxg-launcher-frame,
          #genesys-messenger .mxg-launcher-btn {
            opacity: 0 !important;
            visibility: hidden !important;
            pointer-events: none !important;
          }
        `;

        if (!styleTag) {
          styleTag = document.createElement('style');
          styleTag.id = GENESYS_LAUNCHER_HIDE_STYLE_ID;
          document.head.appendChild(styleTag);
        }

        styleTag.textContent = launcherHideCss;
      } else if (styleTag) {
        styleTag.remove();
      }

      const nativeLauncherFrame = document.querySelector('#genesys-messenger #genesys-mxg-frame');
      if (nativeLauncherFrame) {
        nativeLauncherFrame.style.opacity = isVisible ? '1' : '0';
        nativeLauncherFrame.style.visibility = isVisible ? 'visible' : 'hidden';
        nativeLauncherFrame.style.pointerEvents = isVisible ? 'auto' : 'none';
      }

      const nativeLauncherButton = document.querySelector('#genesys-messenger .mxg-launcher-btn');
      if (nativeLauncherButton) {
        nativeLauncherButton.style.opacity = isVisible ? '1' : '0';
        nativeLauncherButton.style.visibility = isVisible ? 'visible' : 'hidden';
        nativeLauncherButton.style.pointerEvents = isVisible ? 'auto' : 'none';
      }
    };

    const updateGenesysLauncherVisibility = () => {
      if (!genesysChatEnabled.value) {
        if (typeof window[GENESYS_COMMAND_NAME] === 'function') {
          window[GENESYS_COMMAND_NAME]('command', 'Launcher.hide');
        }

        enforceNativeGenesysLauncherVisibility(false);
        return;
      }

      const shouldShowLauncher = genesysMessengerOpen.value || genesysMessengerOpening.value;

      if (typeof window[GENESYS_COMMAND_NAME] === 'function') {
        window[GENESYS_COMMAND_NAME]('command', shouldShowLauncher ? 'Launcher.show' : 'Launcher.hide');
      }

      enforceNativeGenesysLauncherVisibility(shouldShowLauncher);
    };

    const bootstrapGenesys = () => {
      if (!genesysChatEnabled.value) {
        return;
      }

      if (window[GENESYS_COMMAND_NAME] && window._genesysJs === GENESYS_COMMAND_NAME) {
        return;
      }

      window._genesysJs = GENESYS_COMMAND_NAME;
      window[GENESYS_COMMAND_NAME] = window[GENESYS_COMMAND_NAME] || function () {
        (window[GENESYS_COMMAND_NAME].q = window[GENESYS_COMMAND_NAME].q || []).push(arguments);
      };
      window[GENESYS_COMMAND_NAME].t = 1 * new Date();
      window[GENESYS_COMMAND_NAME].c = GENESYS_CONFIG;

      if (document.getElementById(GENESYS_SCRIPT_ID)) {
        return;
      }

      const genesysScript = document.createElement('script');
      genesysScript.id = GENESYS_SCRIPT_ID;
      genesysScript.async = true;
      genesysScript.src = GENESYS_BOOTSTRAP_URL;
      genesysScript.charset = 'utf-8';
      document.head.appendChild(genesysScript);
    };

    const registerGenesysSubscriptions = () => {
      if (!genesysChatEnabled.value) {
        return;
      }

      if (genesysSubscriptionsRegistered.value || typeof window[GENESYS_COMMAND_NAME] !== 'function') {
        return;
      }

      genesysSubscriptionsRegistered.value = true;

      window[GENESYS_COMMAND_NAME]('subscribe', 'Launcher.ready', () => {
        genesysLauncherReady.value = true;
        updateGenesysLauncherVisibility();
      });
      window[GENESYS_COMMAND_NAME]('subscribe', 'Messenger.ready', () => {
        genesysLauncherReady.value = true;
        updateGenesysLauncherVisibility();
      });
      window[GENESYS_COMMAND_NAME]('subscribe', 'Messenger.opened', () => {
        genesysMessengerOpen.value = true;
        genesysMessengerOpening.value = false;
        genesysConversationActive.value = true;
        setGenesysConversationActive(true);
        updateGenesysLauncherVisibility();
      });
      window[GENESYS_COMMAND_NAME]('subscribe', 'Messenger.closed', () => {
        genesysMessengerOpen.value = false;
        genesysMessengerOpening.value = false;
        updateGenesysLauncherVisibility();
      });
      window[GENESYS_COMMAND_NAME]('subscribe', 'MessagingService.conversationCleared', () => {
        genesysMessengerOpen.value = false;
        genesysMessengerOpening.value = false;
        genesysConversationActive.value = false;
        genesysSelectedTreatmentArea.value = null;
        setGenesysConversationActive(false);
        updateGenesysLauncherVisibility();
      });
    };

    const syncGenesysChat = () => {
      if (!genesysChatEnabled.value) {
        updateGenesysLauncherVisibility();
        return;
      }

      bootstrapGenesys();
      registerGenesysSubscriptions();

      if (typeof window[GENESYS_COMMAND_NAME] !== 'function') {
        return;
      }

      window[GENESYS_COMMAND_NAME]('command', 'Database.set', {
        messaging: {
          customAttributes: genesysAttributes.value
        }
      });
      updateGenesysLauncherVisibility();
    };

    const hideGenesysChat = () => {
      if (typeof window[GENESYS_COMMAND_NAME] !== 'function') {
        enforceNativeGenesysLauncherVisibility(false);
        return;
      }

      window[GENESYS_COMMAND_NAME]('command', 'Messenger.close');
      window[GENESYS_COMMAND_NAME]('command', 'Launcher.hide');
      genesysMessengerOpen.value = false;
      genesysMessengerOpening.value = false;
      enforceNativeGenesysLauncherVisibility(false);
    };

    const openGenesysExistingConversation = () => {
      if (!genesysChatEnabled.value) {
        return;
      }

      genesysMessengerOpening.value = true;
      syncGenesysChat();

      if (typeof window[GENESYS_COMMAND_NAME] !== 'function') {
        genesysMessengerOpening.value = false;
        return;
      }

      window[GENESYS_COMMAND_NAME]('command', 'Messenger.open');
    };

    const openGenesysConversation = (treatmentArea) => {
      if (!genesysChatEnabled.value) {
        return;
      }

      genesysSelectedTreatmentArea.value = treatmentArea;
      genesysMessengerOpening.value = true;
      genesysConversationActive.value = true;
      setGenesysConversationActive(true);
      syncGenesysChat();

      if (typeof window[GENESYS_COMMAND_NAME] !== 'function') {
        genesysMessengerOpening.value = false;
        genesysConversationActive.value = false;
        setGenesysConversationActive(false);
        return;
      }

      window[GENESYS_COMMAND_NAME]('command', 'Messenger.open');
    };

    const fetchInitialValue = () => {
      subscriptions.push(isLoggedIn$.subscribe((value) => {
        isLoggedIn.value = value;
      }));
      subscriptions.push(currentUserIdentity$.subscribe((value) => {
        currentUserIdentity.value = value;
      }));
      subscriptions.push(activeDepartment$.subscribe((value) => {
        activeDepartment.value = value;
      }));
    };

    onMounted(() => {
      fetchInitialValue();
      $emitter.on('genesys-treatment-area-selected', openGenesysConversation);
      $emitter.on('genesys-open-existing', openGenesysExistingConversation);
      setGenesysConversationActive(false);
      updateGenesysLauncherVisibility();
    });

    watch(
      [isLoggedIn, genesysAttributes, genesysChatEnabled],
      ([loggedIn, , isGenesysEnabled]) => {
        if (loggedIn && isGenesysEnabled) {
          syncGenesysChat();
          return;
        }

        genesysConversationActive.value = false;
        genesysSelectedTreatmentArea.value = null;
        setGenesysConversationActive(false);
        hideGenesysChat();
      },
      { immediate: true }
    );

    onBeforeUnmount(() => {
      subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      $emitter.off('genesys-treatment-area-selected', openGenesysConversation);
      $emitter.off('genesys-open-existing', openGenesysExistingConversation);
    });

    return {
      isLoggedIn
    };
  },
  mounted() {
    // Allow iframe embedding by removing X-Frame-Options header
    if (document.head.querySelector('meta[http-equiv="X-Frame-Options"]')) {
      document.head.querySelector('meta[http-equiv="X-Frame-Options"]').remove();
    }

    // Add Content-Security-Policy meta tag to allow iframe embedding
    const cspMeta = document.createElement('meta');
    cspMeta.setAttribute('http-equiv', 'Content-Security-Policy');
    cspMeta.setAttribute('content', "frame-ancestors 'self' *");
    document.head.appendChild(cspMeta);

    // Setup iframe communication if we're in an iframe
    if (isInIframe()) {
      // Start URL change notifications
      notifyUrlChanges();

      // Listen for messages from parent
      const cleanup = listenToParentMessages((message) => {
        if (message.type === 'getElements') {
          // Expose specific elements to parent
          exposeElementsToParent(message.selector, message.eventType);
        }
      });

      // Cleanup on component unmount using mitt
      const emitter = mitt();
      emitter.on('beforeDestroy', cleanup);
    }

    let host = window.location.host;
    document.title = 'Mediq Voorschrijfportaal';

    // Google Tag Manager
    let gtmScript = document.createElement('script');
    gtmScript.innerHTML =
      "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-5JZVM3L');";
    document.head.appendChild(gtmScript);
    let gtmNoScript = document.createElement('noscript');
    gtmNoScript.innerHTML =
      "<iframe src='https://www.googletagmanager.com/ns.html?id=GTM-5JZVM3L' height='0' width='0' style='display:none;visibility:hidden'></iframe>";
    document.body.appendChild(gtmNoScript);
    // End Google Tag Manager

    window.addEventListener('message', (event) => {
      // Verify the origin of the message for security
      if (!event.origin.match(/^https?:\/\/(.*\.mediq\.nl|medbis\.localhost)/)) return;

      // Handle messages from parent
      if (event.data.type === 'navigation') {
        this.$router.push(event.data.path);
      }
    });
  }
};
</script>
